import { useEffect } from "react";
import { trackEvent } from "@/lib/analytics";

/**
 * Attaches a single delegated click listener to the document to track:
 *  - click-to-call  (tel: links)
 *  - email clicks   (mailto: links)
 *  - primary CTA    (elements with data-track-cta)
 *
 * Each interaction fires exactly once per click via delegation.
 */
export function useGlobalClickTracking() {
  useEffect(() => {
    function handler(e: MouseEvent) {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a");

      if (anchor) {
        const href = anchor.getAttribute("href") || "";

        if (href.startsWith("tel:")) {
          trackEvent("call_click", { number: href.replace("tel:", "") });
          return;
        }

        if (href.startsWith("mailto:")) {
          trackEvent("email_click", { address: href.replace("mailto:", "") });
          return;
        }
      }

      // CTA tracking via data attribute
      const ctaEl = target.closest("[data-track-cta]");
      if (ctaEl) {
        const label = ctaEl.getAttribute("data-track-cta") || "unknown";
        trackEvent("cta_click", { label });
      }
    }

    document.addEventListener("click", handler, { passive: true });
    return () => document.removeEventListener("click", handler);
  }, []);
}
